import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBasket, CupSoda, Droplets, SprayCan, Zap, Check } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { useContent } from "../lib/content";

const CATS = [
    {
        id: "alimentos",
        label: "Alimentos",
        icon: ShoppingBasket,
        items: ["Lanches e snacks", "Pão e padaria", "Congelados prontos", "Biscoitos e doces", "Temperos do dia a dia"],
        chip: "bg-[#0E6B3F] text-[#F7F4EC]",
    },
    {
        id: "bebidas",
        label: "Bebidas",
        icon: CupSoda,
        items: ["Refrigerantes gelados", "Sucos e águas", "Cervejas", "Cafés e energéticos"],
        chip: "bg-[#F57A3A] text-[#F7F4EC]",
    },
    {
        id: "higiene",
        label: "Higiene",
        icon: Droplets,
        items: ["Sabonetes e shampoos", "Creme dental", "Papel higiênico", "Cuidados diários"],
        chip: "bg-[#F1C75B] text-[#1A231E]",
    },
    {
        id: "limpeza",
        label: "Limpeza",
        icon: SprayCan,
        items: ["Detergentes", "Sabão em pó", "Papel toalha e cozinha", "Sacolas e lixo"],
        chip: "bg-[#0E6B3F] text-[#F7F4EC]",
    },
    {
        id: "conveniencia",
        label: "Conveniência",
        icon: Zap,
        items: ["Gelo e carvão", "Pilhas e lâmpadas", "Petiscos pet", "Sorvetes"],
        chip: "bg-[#F57A3A] text-[#F7F4EC]",
    },
];

export default function Categorias() {
    const [active, setActive] = useState("alimentos");
    const t = useContent().categorias;
    const current = CATS.find((c) => c.id === active);
    const items = (t.items?.[current.id] || "")
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean);

    return (
        <section id="categorias" className="scroll-mt-24 bg-[#F7F4EC] py-20 sm:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <Reveal className="max-w-2xl">
                    <SectionTag tone="orange">Estoque completo</SectionTag>
                    <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#1A231E]">
                        {t.title}
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-[#1A231E]/70 leading-relaxed">
                        {t.subtitle}
                    </p>
                </Reveal>

                <div className="mt-10 flex flex-wrap gap-3.5" data-testid="cat-display-items">
                    {CATS.map((c, i) => {
                        const isActive = active === c.id;
                        return (
                            <Reveal key={c.id} delay={i * 0.06}>
                                <button
                                    data-testid={`cat-${c.id}`}
                                    onClick={() => setActive(c.id)}
                                    className={`inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-base font-extrabold transition-all duration-200 hover:-translate-y-1 ${
                                        isActive
                                            ? `${c.chip} sticker`
                                            : "bg-white/60 text-[#1A231E]/70 border-2 border-[#1A231E]/12 hover:border-[#1A231E]/30"
                                    }`}
                                >
                                    <c.icon className="w-5 h-5" />
                                    {c.label}
                                </button>
                            </Reveal>
                        );
                    })}
                </div>

                <div className="mt-8 relative">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={current.id}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.3 }}
                            className="rounded-[1.75rem] border-2 border-[#1A231E]/12 bg-[#EFEAE0] p-7 sm:p-9 sticker-soft"
                        >
                            <span className="font-caveat text-3xl text-[#0E6B3F]">
                                prateleira de {current.label.toLowerCase()}:
                            </span>
                            <ul className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
                                {items.map((it) => (
                                    <li
                                        key={it}
                                        className="inline-flex items-center gap-2 text-base sm:text-lg font-semibold text-[#1A231E]/85"
                                    >
                                        <Check className="w-5 h-5 text-[#F57A3A]" strokeWidth={3} />
                                        {it}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
